import { apiEndpoints, ApiEndpoint } from '../data/apiEndpoints';
import { API_BASE_URL } from '../config/api';

interface PostmanHeader {
  key: string;
  value: string;
  type: string;
}

interface PostmanQueryParam {
  key: string;
  value: string;
  description?: string;
}

interface PostmanUrl {
  raw: string;
  protocol?: string;
  host: string[];
  path: string[];
  query?: PostmanQueryParam[];
  variable?: Array<{ key: string; value: string; description?: string }>;
}

interface PostmanFormData {
  key: string;
  value: string;
  type: string;
  description?: string;
}

interface PostmanUrlEncoded {
  key: string;
  value: string;
  description?: string;
}

interface PostmanBody {
  mode: string;
  formdata?: PostmanFormData[];
  urlencoded?: PostmanUrlEncoded[];
  raw?: string;
  options?: { raw: { language: string } };
}

interface PostmanRequest {
  method: string;
  header: PostmanHeader[];
  url: PostmanUrl;
  body?: PostmanBody;
  description?: string;
  auth?: { type: string };
}

interface PostmanItem {
  name: string;
  request: PostmanRequest;
}

interface PostmanFolder {
  name: string;
  description?: string;
  item: PostmanItem[];
}

interface PostmanCollection {
  info: {
    name: string;
    description: string;
    schema: string;
  };
  auth: {
    type: string;
    bearer: Array<{ key: string; value: string; type: string }>;
  };
  variable: Array<{ key: string; value: string; description?: string }>;
  item: PostmanFolder[];
}

const STEP_LABELS: Record<number, string> = {
  1: 'Step 1 - Authentication',
  2: 'Step 2 - File Validation',
  3: 'Step 3 - Data Extraction',
  4: 'Step 4 - Run Analysis',
  5: 'Step 5 - Download Results',
  6: 'Step 6 - Create Map Layers',
};

const REPORT_STEP_LABELS: Record<number, string> = {
  1: 'Report Demo - Step 1: Authorize',
  2: 'Report Demo - Step 2: Select Project/Layout/Config',
  4: 'Report Demo - Step 4: Check Prerequisites',
  5: 'Report Demo - Step 5: Configure Pile Binning',
  7: 'Report Demo - Step 7: Generate Report',
};

/**
 * Extracts path parameter names from a URL template like
 * "/tools/api/v1/terrain-pro/validate/{job_id}/data"
 */
function extractPathParams(path: string): string[] {
  const matches = path.match(/\{(\w+)\}/g);
  return matches ? matches.map(m => m.replace(/[{}]/g, '')) : [];
}

/**
 * Determines whether a POST endpoint should use multipart form-data
 * (i.e., has a file-type parameter).
 */
function hasFileParam(endpoint: ApiEndpoint): boolean {
  return (endpoint.parameters ?? []).some(p => p.type === 'file');
}

function buildUrl(endpoint: ApiEndpoint): PostmanUrl {
  const pathParams = extractPathParams(endpoint.path);
  const rawPath = endpoint.path.replace(/\{(\w+)\}/g, ':$1');
  const segments = rawPath.split('/').filter(Boolean);
  // A trailing "/" in the source path produces a trailing empty string from
  // split('/') that filter(Boolean) then strips — losing the slash entirely,
  // since Postman reconstructs its URL bar from `path`, not `raw`, so `raw`
  // alone having the slash isn't enough. Re-add it as an explicit empty
  // segment, Postman's own convention for representing a trailing slash.
  if (rawPath.length > 1 && rawPath.endsWith('/')) {
    segments.push('');
  }

  const url: PostmanUrl = {
    raw: `{{base_url}}${rawPath}`,
    host: ['{{base_url}}'],
    path: segments,
  };

  if (pathParams.length) {
    url.variable = pathParams.map(key => {
      const paramDef = (endpoint.parameters ?? []).find(p => p.name === key);
      return {
        key,
        value: '',
        description: paramDef?.description ?? '',
      };
    });
  }

  // GET requests treat every non-path param as a query param; other methods
  // only do so for params explicitly marked `in: 'query'` (e.g. ?version=
  // on a POST) — everything else goes in the request body instead.
  const queryParams = (endpoint.parameters ?? []).filter(
    p => !pathParams.includes(p.name) && (endpoint.method === 'GET' || p.in === 'query')
  );
  if (queryParams.length) {
    url.query = queryParams.map(p => ({
      key: p.name,
      value: p.default ?? '',
      description: `${p.required ? '(required) ' : ''}${p.description}`,
    }));
    url.raw += '?' + queryParams.map(p => `${p.name}=${p.default ?? ''}`).join('&');
  }

  return url;
}

function resolvedContentType(endpoint: ApiEndpoint): string {
  if (endpoint.contentType) return endpoint.contentType;
  return hasFileParam(endpoint) ? 'multipart/form-data' : 'application/json';
}

function buildBody(endpoint: ApiEndpoint): PostmanBody | undefined {
  if (endpoint.method !== 'POST') return undefined;

  const pathParams = extractPathParams(endpoint.path);
  const bodyParams = (endpoint.parameters ?? []).filter(
    p => !pathParams.includes(p.name) && p.in !== 'query'
  );

  if (!bodyParams.length) return undefined;

  const contentType = resolvedContentType(endpoint);

  if (contentType === 'multipart/form-data') {
    return {
      mode: 'formdata',
      formdata: bodyParams.map(p => ({
        key: p.name,
        value: '',
        type: p.type === 'file' ? 'file' : 'text',
        description: `${p.required ? '(required) ' : ''}${p.description}`,
      })),
    };
  }

  if (contentType === 'application/x-www-form-urlencoded') {
    return {
      mode: 'urlencoded',
      urlencoded: bodyParams.map(p => ({
        key: p.name,
        value: '',
        description: `${p.required ? '(required) ' : ''}${p.description}`,
      })),
    };
  }

  const sampleBody: Record<string, unknown> = {};
  for (const p of bodyParams) {
    if (p.type === 'array') sampleBody[p.name] = [];
    else if (p.type === 'integer') sampleBody[p.name] = 0;
    else if (p.type === 'number') sampleBody[p.name] = 0;
    else if (p.type === 'boolean') sampleBody[p.name] = true;
    else sampleBody[p.name] = '';
  }

  return {
    mode: 'raw',
    raw: JSON.stringify(sampleBody, null, 2),
    options: { raw: { language: 'json' } },
  };
}

function buildItem(endpoint: ApiEndpoint): PostmanItem {
  const headers: PostmanHeader[] = [
    { key: 'Accept', value: 'application/json', type: 'text' },
  ];

  const contentType = resolvedContentType(endpoint);
  if (endpoint.method === 'POST' && contentType !== 'multipart/form-data') {
    headers.push({ key: 'Content-Type', value: contentType, type: 'text' });
  }

  if (endpoint.authScheme === 'Bearer-PP') {
    headers.push({ key: 'Authorization', value: 'Bearer-PP {{auth_token}}', type: 'text' });
  } else if (endpoint.authScheme === 'token-only') {
    headers.push({ key: 'Authorization', value: '{{auth_token}}', type: 'text' });
  }

  return {
    name: endpoint.title,
    request: {
      method: endpoint.method,
      header: headers,
      url: buildUrl(endpoint),
      body: buildBody(endpoint),
      description: endpoint.description,
      // Email Login needs no token at all, and Bearer-PP/token-only requests set
      // their own Authorization header above — all must override the collection-level
      // bearer auth, otherwise Postman applies its inherited "Authorization: Bearer
      // <token>" on top of (or in place of) the manual header, producing a
      // malformed/duplicate Authorization value that crashes
      // JWTAuthenticationMiddleware's `token.split(' ')` unpacking with a 500
      // instead of a clean auth error.
      ...(endpoint.authScheme === 'none' || endpoint.authScheme === 'Bearer-PP' || endpoint.authScheme === 'token-only'
        ? { auth: { type: 'noauth' } }
        : {}),
    },
  };
}

export function generatePostmanCollection(): PostmanCollection {
  const stepGroups = new Map<string, PostmanItem[]>();

  // Only ship endpoints one of the two demo pages actually calls today. A number
  // of catalogue entries exist purely for reference (drafted-but-unused resource
  // endpoints, or legacy report/DOCX routes superseded by the clean API) and
  // would otherwise clutter the collection with requests nobody importing it to
  // follow along with a demo can exercise end-to-end.
  const demoEndpoints = apiEndpoints.filter(
    ep => (ep.usedBy ?? []).some(u => u === 'api-demo' || u === 'report-demo')
  );

  for (const ep of demoEndpoints) {
    let folderName: string;
    if (ep.step) {
      folderName = STEP_LABELS[ep.step] ?? `Step ${ep.step}`;
    } else if (ep.reportDemoStep) {
      // Any endpoint tagged reportDemoStep belongs in a "Report Demo" folder,
      // regardless of which app implements it — this now includes clean
      // terrain-pro-api endpoints like Generate Nextracker Advanced Report,
      // not just the legacy/vec-tiles ones.
      folderName = REPORT_STEP_LABELS[ep.reportDemoStep] ?? `Report Demo - Step ${ep.reportDemoStep}`;
    } else {
      folderName = 'Resource Management';
    }

    if (!stepGroups.has(folderName)) {
      stepGroups.set(folderName, []);
    }
    stepGroups.get(folderName)!.push(buildItem(ep));
  }

  const folders: PostmanFolder[] = Array.from(stepGroups.entries()).map(
    ([name, items]) => ({ name, item: items })
  );

  return {
    info: {
      name: 'Terabase Terrain Pro API',
      description:
        'Complete Postman collection for the Terrain Pro API, covering both the API Demo and Report Demo pages.\n\n' +
        '## Setup\n' +
        '1. Set the `base_url` variable to your environment URL.\n' +
        '2. Set the `auth_token` variable to your JWT Bearer token.\n' +
        '3. Folders "Step 1" through "Step 6" are the core, clean Terrain Pro API workflow used by API Demo:\n' +
        '   Authenticate → Validate File → Extract Data → Run Analysis → Download Results → Create Map Layers.\n' +
        '4. Folders prefixed "Report Demo - Step N" are the endpoints Report Demo calls (heatmap/pile-reveal prerequisites,\n' +
        '   pile-binning edits, and report generation) — mostly legacy terrain_pro / vec_tiles / pile-binning functionality\n' +
        '   proxied under /tools/api/v1/terrain-pro/reports/... so it authenticates with the same token as everything else,\n' +
        '   plus a couple of endpoints implemented natively in the clean API. A few legacy requests still use a\n' +
        '   non-standard "Bearer-PP" Authorization header instead of "Bearer" — already set on those individual requests.\n' +
        '5. "Resource Management" holds shared read endpoints (projects/layouts/configurations/jobs) used by both pages.',
      schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
    },
    auth: {
      type: 'bearer',
      bearer: [{ key: 'token', value: '{{auth_token}}', type: 'string' }],
    },
    variable: [
      {
        key: 'base_url',
        value: API_BASE_URL,
        description: 'Base URL for the Terrain Pro API',
      },
      {
        key: 'auth_token',
        value: '',
        description: 'JWT Bearer token from Cognito authentication',
      },
    ],
    item: folders,
  };
}

export function downloadPostmanCollection(): void {
  const collection = generatePostmanCollection();
  const json = JSON.stringify(collection, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'Terabase_Terrain_Pro_API.postman_collection.json';
  document.body.appendChild(anchor);
  anchor.click();

  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
